import React, { useState, useEffect } from 'react';
import { Modal, Card, Checkbox, Badge, Tooltip, Pagination, Button, Input, Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../shared/images/AvatarS3';
import moment from 'moment';
import { urgencyColors } from '../configs/task.config';
import { projectStatusColors } from '../configs/project.config';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

const AddTasksToMilestoneModal = ({
  visible,
  onCancel,
  onConfirm,
  milestone,
  projectTasks,
  loading,
}) => {
  const windowWidth = useWindowWidth();
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filter available tasks (not already in milestone)
  const availableTasks = projectTasks.filter(
    (task) => !milestone?.tasks?.some((mt) => mt.id === task.id)
  );

  // Apply search filter
  const filteredTasks = availableTasks.filter((task) =>
    task.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Calculate pagination
  const totalTasks = filteredTasks.length;
  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  const handleTaskSelect = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    const allTaskIds = filteredTasks.map((task) => task.id);
    setSelectedTaskIds(allTaskIds);
  };

  const handleClearSelection = () => {
    setSelectedTaskIds([]);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleConfirm = () => {
    onConfirm(selectedTaskIds);
    handleCancel();
  };

  const handleCancel = () => {
    setSelectedTaskIds([]);
    setSearchText('');
    setPagination({ current: 1, pageSize: 10 });
    onCancel();
  };

  const milestoneId = milestone?.id;
  useEffect(() => {
    if (visible) {
      setSelectedTaskIds([]);
      setSearchText('');
      setPagination({ current: 1, pageSize: 10 });
    }
  }, [visible, milestoneId]);

  return (
    <Modal
      title={<span style={{ fontSize: windowWidth < 768 ? '16px' : '16px' }}>{`Add Tasks to "${milestone?.name}"`}</span>}
      open={visible}
      onCancel={handleCancel}
      width={windowWidth < 768 ? '95%' : 900}
      centered
      bodyStyle={{ padding: windowWidth < 768 ? '16px' : '24px' }}
      footer={
        <div style={{
          display: 'flex',
          flexDirection: windowWidth < 768 ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: windowWidth < 768 ? 'flex-start' : 'center',
          gap: windowWidth < 768 ? '12px' : '0',
        }}>
          <Text style={{
            color: '#64748b',
            alignSelf: windowWidth < 768 ? 'flex-start' : 'center',
          }}>
            {selectedTaskIds.length} task(s) selected
          </Text>
          <div style={{
            display: 'flex',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            gap: 8,
            width: windowWidth < 768 ? '100%' : 'auto',
          }}>
            <Button
              onClick={handleCancel}
              style={{
                fontSize: '14px',
                height: '40px',
                borderRadius: '5px',
                width: windowWidth < 768 ? '100%' : 'auto',
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={selectedTaskIds.length === 0 || loading}
              onClick={handleConfirm}
              style={{
                fontSize: '14px',
                height: '40px',
                borderRadius: '5px',
                backgroundColor: '#3b82f6',
                width: windowWidth < 768 ? '100%' : 'auto',
              }}
            >
              Add Selected Tasks
            </Button>
          </div>
        </div>
      }
    >
      <div>
        <Text style={{ display: 'block', marginBottom: 16, color: '#64748b' }}>
          Select tasks from the current project to add to this milestone:
        </Text>

        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            alignItems: windowWidth < 768 ? 'stretch' : 'center',
            gap: windowWidth < 768 ? '12px' : '8px',
            justifyContent: 'space-between',
          }}>
            <div style={{
              display: 'flex',
              gap: 8,
              width: windowWidth < 768 ? '100%' : 'auto',
              flexWrap: windowWidth < 768 ? 'nowrap' : 'wrap',
            }}>
              <Button
                size="small"
                onClick={handleSelectAll}
                disabled={filteredTasks.length === 0}
                style={{
                  fontSize: windowWidth < 768 ? '12px' : '13px',
                  flex: windowWidth < 768 ? 1 : 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: windowWidth < 768 ? '0 8px' : undefined,
                }}
              >
                {windowWidth < 768 ? `All (${filteredTasks.length})` : `Select All (${filteredTasks.length})`}
              </Button>
              <Button
                size="small"
                onClick={handleClearSelection}
                disabled={selectedTaskIds.length === 0}
                style={{
                  fontSize: windowWidth < 768 ? '12px' : '13px',
                  color: '#64748b',
                  flex: windowWidth < 768 ? 1 : 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: windowWidth < 768 ? '0 8px' : undefined,
                }}
              >
                {windowWidth < 768 ? 'Clear' : 'Clear Selection'}
              </Button>
            </div>
            <Input
              placeholder="Search tasks"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: windowWidth < 768 ? '100%' : 250,
                height: '36px',
                borderRadius: '5px',
              }}
              allowClear
            />
          </div>
        </div>

        {/* Task List */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: 16,
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {paginatedTasks.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px dashed #cbd5e1',
              }}
            >
              <Text style={{ color: '#94a3b8', fontSize: '14px' }}>
                {availableTasks.length === 0
                  ? 'All project tasks are already assigned to this milestone.'
                  : 'No tasks found matching your search.'}
              </Text>
            </div>
          ) : (
            paginatedTasks.map((task) => (
              <Card
                key={task.id}
                hoverable
                onClick={() => handleTaskSelect(task.id)}
                style={{
                  borderRadius: '7px',
                  border: selectedTaskIds.includes(task.id)
                    ? '2px solid #3b82f6'
                    : '1px solid #e5edf4',
                  cursor: 'pointer',
                  backgroundColor: selectedTaskIds.includes(task.id) ? '#eff6ff' : 'white',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Checkbox
                    checked={selectedTaskIds.includes(task.id)}
                    onChange={() => handleTaskSelect(task.id)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: windowWidth < 768 ? 'column' : 'row',
                      justifyContent: 'space-between',
                      alignItems: windowWidth < 768 ? 'flex-start' : 'flex-start',
                      gap: windowWidth < 768 ? '12px' : '0',
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tooltip title={task.urgency ? `URGENCY: ${task.urgency}` : 'NO URGENCY'}>
                            <Badge
                              dot
                              color={urgencyColors[task.urgency] || 'white'}
                              className="custom-badge"
                            />
                          </Tooltip>
                          <Text strong style={{ fontSize: '15px' }}>
                            {task.name}
                          </Text>
                        </div>
                        {task.requestType && (
                          <Text style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '20px' }}>
                            {task.requestType
                              .split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                              .join(' ')}
                          </Text>
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        flexDirection: windowWidth < 768 ? 'column' : 'row',
                        gap: windowWidth < 768 ? '8px' : '16px',
                        alignItems: windowWidth < 768 ? 'flex-start' : 'center',
                        width: windowWidth < 768 ? '100%' : 'auto',
                      }}>
                        {task.dueDate && (
                          <div style={{
                            display: 'flex',
                            flexDirection: windowWidth < 768 ? 'row' : 'column',
                            alignItems: windowWidth < 768 ? 'center' : 'flex-end',
                            gap: windowWidth < 768 ? '8px' : '0',
                          }}>
                            <Text style={{ fontSize: '11px', color: '#64748b' }}>Due:</Text>
                            <Text style={{ fontSize: '12px' }}>
                              {moment(task.dueDate).format('MMM D, YYYY')}
                            </Text>
                          </div>
                        )}

                        {task.assigneeUsers && task.assigneeUsers.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {task.assigneeUsers.slice(0, 3).map((user, idx) => (
                              <Tooltip key={user.id || idx} title={startCase(user.name || user.username)}>
                                <div style={{ display: 'inline-flex' }}>
                                  <AvatarS3
                                    imgKey={user.avatar && user.avatar.key}
                                    username={user.name || user.username}
                                    avatarProps={{ size: 27 }}
                                  />
                                </div>
                              </Tooltip>
                            ))}
                            {task.assigneeUsers.length > 3 && (
                              <Text style={{ fontSize: '11px', color: '#64748b' }}>
                                +{task.assigneeUsers.length - 3}
                              </Text>
                            )}
                          </div>
                        )}

                        {task.status && (
                          <Tag color={projectStatusColors[task.status] || 'default'} style={{ margin: 0 }}>
                            {task.status
                              ?.split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ') || 'N/A'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {filteredTasks.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={totalTasks}
              onChange={handlePaginationChange}
              showSizeChanger={windowWidth >= 768}
              pageSizeOptions={['10', '20', '50']}
              showTotal={windowWidth >= 768 ? (total, range) => `${range[0]}-${range[1]} of ${total} tasks` : undefined}
              simple={windowWidth < 768}
              size={windowWidth < 768 ? 'small' : 'default'}
            />
          </div>
        )}

        {selectedTaskIds.length > 0 && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: '#f0f9ff',
              borderRadius: 6,
            }}
          >
            <Text style={{ color: '#0369a1' }}>
              {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''} selected
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddTasksToMilestoneModal;
